import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  getReport,
  saveProfileSelection,
} from "~/lib/server-fns";
import { fetchAllProfiles } from "~/lib/fetch-profiles";
import {
  getMatchingProfiles,
  SECTION_LABELS,
  profiles as fallbackProfiles,
} from "~/data/profiles";
import { ProfileTabs } from "~/components/report/ProfileTabs";
import { KeyModelContent } from "~/components/report/KeyModelContent";
import { ProfileContent } from "~/components/report/ProfileContent";
import { ProfileInvitations } from "~/components/report/ProfileInvitations";
import { RelatedProfilesSidebar } from "~/components/report/RelatedProfilesSidebar";
import { MobileDualBottomBar } from "~/components/report/MobileDualBottomBar";
import { captureHandledException } from "~/lib/handled-errors";

export const Route = createFileRoute("/report/both/$id")({
  component: BothProfilesPage,
  loader: async ({ params }) => {
    async function loadSanityProfiles() {
      try {
        return await fetchAllProfiles();
      } catch (err) {
        await captureHandledException(err, {
          feature: "report",
          action: "load-sanity-profiles",
          layer: "loader",
          fallback: "static-profiles",
          extra: { route: "report.both", reportId: params.id },
        });
        return null;
      }
    }

    const [data, sanityProfiles] = await Promise.all([
      getReport({ data: params.id }),
      loadSanityProfiles(),
    ]);

    if (data.selectedProfile) {
      throw redirect({
        to: "/report/$id",
        params: { id: params.id },
        search: { profile: undefined, force_dual: undefined },
      });
    }

    const allProfiles = sanityProfiles?.length
      ? sanityProfiles
      : fallbackProfiles;

    const scores = {
      identity: data.identityScore,
      purpose: data.purposeScore,
      community: data.communityScore,
    };
    const result = getMatchingProfiles(scores, true, allProfiles);

    if (!result.isDualProfile) {
      throw redirect({
        to: "/report/$id",
        params: { id: params.id },
        search: { profile: undefined, force_dual: undefined },
      });
    }

    return { ...data, result, allProfiles };
  },
  errorComponent: () => (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl text-core-orange mb-4">Report Not Found</h1>
        <p className="text-white/60 mb-6">
          This report link may be invalid or expired.
        </p>
        <a
          href="/"
          className="inline-block bg-core-orange text-white font-bold py-2 px-6 rounded-full hover:opacity-90 transition-opacity"
        >
          Take the Assessment
        </a>
      </div>
    </div>
  ),
});

function BothProfilesPage() {
  const data = Route.useLoaderData();
  const { primary, secondary } = data.result;
  const [activeId, setActiveId] = useState(primary.id);

  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const bothProfiles = [primary, secondary!] as [
    typeof primary,
    typeof secondary & object,
  ];
  const activeProfile = bothProfiles.find((p) => p.id === activeId) ?? primary;
  const otherProfile =
    bothProfiles.find((p) => p.id !== activeId) ?? secondary!;

  async function handleSelectProfile() {
    setSaving(true);
    try {
      await saveProfileSelection({
        data: { reportId: data.id, profileId: activeProfile.id },
      });
      navigate({
        to: "/report/$id",
        params: { id: data.id },
        search: { profile: undefined, force_dual: undefined },
      });
    } catch (err) {
      console.error("Profile selection failed:", err);
      await captureHandledException(err, {
        feature: "report",
        action: "select-profile",
        layer: "client",
        fallback: "toast",
        extra: { reportId: data.id, profileId: activeProfile.id, route: "report.both" },
      });
      toast.error("Something went wrong saving your profile. Please try again.");
      setSaving(false);
    }
  }

  const ctaSectionRef = useRef<HTMLElement>(null);
  const [ctaVisible, setCtaVisible] = useState(false);

  useEffect(() => {
    const el = ctaSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setCtaVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex-1 pb-20 lg:pb-0">
      <div className="max-w-[1080px] mx-auto">
        {/* Header */}
        <section className="flex flex-col items-center gap-3 px-4 sm:px-15 py-8">
          <h1 className="text-4xl text-core-orange tracking-widest text-center">
            {SECTION_LABELS.coreProfile}
          </h1>
          <p className="text-white/60 text-center">
            Choose the profile that resonates with you
          </p>
        </section>

        {/* Tabs */}
        <ProfileTabs
          profiles={bothProfiles}
          activeId={activeId}
          onSelect={setActiveId}
        />

        {/* Tagline */}
        <div className="flex items-center justify-center px-4 sm:px-15 py-4">
          <span className="text-core-orange text-sm text-center">
            {activeProfile.tagline}
          </span>
        </div>

        {/* The Model */}
        <section className="p-4 sm:px-15 py-10 sm:py-12">
          <div className="w-full h-px bg-white/10 mb-6" />
          <h3 className="text-2xl text-core-orange tracking-wider mb-6">
            {SECTION_LABELS.keyModel}
          </h3>
          <KeyModelContent
            content={activeProfile.keyModel.content}
            className="text-white/80 leading-relaxed"
          />
        </section>

        {/* Profile Content */}
        <div className="p-4 lg:py-6">
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            <div className="min-w-0 lg:order-1 px-4 pl-15">
              <ProfileContent profile={activeProfile} />
              <ProfileInvitations profile={activeProfile} />
            </div>
            <RelatedProfilesSidebar
              variant="report-both"
              activeProfile={activeProfile}
              profiles={data.allProfiles}
              prioritizedProfileId={otherProfile.id}
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="px-4 sm:px-[60px] py-6">
          <div className="w-full h-px bg-white/10" />
        </div>
        <section
          ref={ctaSectionRef}
          className="flex flex-col items-center gap-4 px-4 sm:px-[60px] py-8"
        >
          <p className="text-white/60 text-center">
            Does this profile resonate with you?
          </p>
          <button
            type="button"
            onClick={handleSelectProfile}
            disabled={saving}
            className="px-10 py-4 rounded-full bg-core-orange text-white text-lg font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {saving ? "Saving..." : "This Is My Profile"}
          </button>
          <button
            type="button"
            onClick={() => setActiveId(otherProfile.id)}
            className="text-white/40 text-sm hover:text-white/60 transition-colors"
          >
            or switch to {otherProfile.name} {"\u2192"}
          </button>
        </section>
      </div>

      {/* Sticky bottom bar — desktop only, hides when inline CTA is visible */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-core-dark-gray/95 backdrop-blur-sm transition-transform duration-300 hidden lg:block ${
          ctaVisible ? "translate-y-full" : "translate-y-0"
        }`}
      >
        <div className="max-w-[1080px] mx-auto flex items-center justify-between gap-4 px-4 sm:px-[60px] py-4">
          <span className="text-white/60 text-sm hidden sm:block">
            Does this profile resonate with you?
          </span>
          <div className="flex items-center gap-4 ml-auto">
            <button
              type="button"
              onClick={() => setActiveId(otherProfile.id)}
              className="text-white/40 text-sm hover:text-white/60 transition-colors"
            >
              Switch to {otherProfile.name}
            </button>
            <button
              type="button"
              onClick={handleSelectProfile}
              disabled={saving}
              className="px-8 py-3 rounded-full bg-core-orange text-white font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {saving ? "Saving..." : "This Is My Profile"}
            </button>
          </div>
        </div>
      </div>

      {/* Sticky bottom bar — mobile only */}
      <MobileDualBottomBar
        onConfirmProfile={handleSelectProfile}
        saving={saving}
        visible={!ctaVisible}
      />
    </div>
  );
}
